import React, { useEffect, useState } from "react";
import { Breadcrumbs } from "../AdminLayout";
import { enquiriesApi } from "./api";
import { formatErr } from "../api";
import { toast, Toaster } from "sonner";
import { Inbox, Search, Mail, Trash2, X as XIcon } from "lucide-react";

const TABS = ["new", "read", "replied", "spam", "archived"];

export default function EnquiryInbox() {
  const [tab, setTab] = useState("new");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const reload = () => {
    enquiriesApi.list({ status: tab, q: q || undefined, limit: 200 }).then(setRows).catch(e => toast.error(formatErr(e.response?.data?.detail)));
    enquiriesApi.counts().then(setCounts).catch(() => {});
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);
  useEffect(() => { const t = setTimeout(reload, 300); return () => clearTimeout(t); }, [q]);

  const move = async (id, status) => {
    try { await enquiriesApi.update(id, { status }); toast.success(`Marked as ${status}`); setSelected(null); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const saveNotes = async (id, notes) => {
    try { await enquiriesApi.update(id, { internal_notes: notes }); toast.success("Notes saved"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try { await enquiriesApi.remove(id); toast.success("Deleted"); setSelected(null); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="enquiry-inbox">
      <Toaster position="top-right" richColors/>
      <Breadcrumbs items={[{ label: "Site" }, { label: "Enquiries" }]}/>
      <div className="flex items-center justify-between mt-4 mb-6">
        <div className="font-serif text-3xl text-navy flex items-center gap-3"><Inbox size={22}/> Enquiry Inbox</div>
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone2"/>
          <input data-testid="enq-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="pl-8 pr-3 py-2 border border-line text-sm w-64"/>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-line mb-6">
        {TABS.map(t => (
          <button key={t} data-testid={`enq-tab-${t}`} onClick={() => setTab(t)}
                  className={`mono text-[10px] uppercase tracking-widest px-4 py-2 border-b-2 -mb-px ${tab === t ? "border-navy text-navy" : "border-transparent text-stone2 hover:text-navy"}`}>
            {t} {counts[t] ? `(${counts[t]})` : ""}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-5">
          {rows.length === 0 ? (
            <div className="bg-white border border-line p-8 text-center text-sm text-stone2">Nothing in {tab}.</div>
          ) : (
            <ul className="space-y-2">
              {rows.map(r => (
                <li key={r.id} data-testid={`enq-row-${r.id}`}
                    onClick={() => setSelected(r)}
                    className={`bg-white border cursor-pointer px-4 py-3 ${selected?.id === r.id ? "border-navy" : "border-line hover:border-navy"}`}>
                  <div className="text-sm text-navy truncate">{r.subject || "(no subject)"}</div>
                  <div className="text-xs text-stone2 truncate">{r.name} · {r.email}</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-stone2 mt-1">{r.created_at}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="col-span-12 lg:col-span-7">
          {selected ? (
            <div className="bg-white border border-line p-6" data-testid="enq-detail">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="min-w-0">
                  <div className="font-serif text-xl text-navy">{selected.subject || "(no subject)"}</div>
                  <div className="text-xs text-stone2 mt-1">{selected.name} · <a href={`mailto:${selected.email}`} className="text-navy underline">{selected.email}</a> {selected.phone && `· ${selected.phone}`}</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-stone2 mt-1">{selected.created_at}</div>
                </div>
                <button onClick={() => setSelected(null)} className="text-stone2 hover:text-navy"><XIcon size={16}/></button>
              </div>
              <div className="text-sm text-navy whitespace-pre-wrap leading-relaxed border-l-2 border-line pl-4">{selected.message || "(no message)"}</div>

              {selected.recaptcha && !selected.recaptcha.configured && (
                <div data-testid="enq-recaptcha-not-configured" className="mt-4 border border-[#D97706]/30 bg-[#D97706]/5 px-3 py-2 mono text-[10px] uppercase tracking-widest text-[#8B5A00]">
                  reCAPTCHA not yet configured — verification skipped for this submission.
                </div>
              )}

              <div className="mt-6">
                <div className="mono text-[10px] uppercase tracking-widest text-prestige mb-2">Internal notes</div>
                <textarea data-testid="enq-notes" defaultValue={selected.internal_notes || ""} onBlur={e => saveNotes(selected.id, e.target.value)} rows={3} className="w-full border border-line px-3 py-2 text-sm"/>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <a data-testid="enq-reply" href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "your enquiry")}`}
                   className="bg-navy hover:bg-navy-hover text-white text-sm px-4 py-2 inline-flex items-center gap-2"><Mail size={13}/> Compose reply</a>
                {TABS.filter(s => s !== selected.status).map(s => (
                  <button key={s} data-testid={`enq-move-${s}`} onClick={() => move(selected.id, s)} className="border border-line hover:border-navy text-navy text-xs px-3 py-1.5 mono uppercase tracking-widest">Move to {s}</button>
                ))}
                <button data-testid="enq-delete" onClick={() => remove(selected.id)} className="border border-[#9F1239] text-[#9F1239] text-xs px-3 py-1.5 inline-flex items-center gap-1"><Trash2 size={12}/> Delete</button>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-line p-8 text-center text-sm text-stone2">Select an enquiry to view details.</div>
          )}
        </div>
      </div>
    </div>
  );
}
